import dayjs from 'dayjs';
import { ISelectOption } from '@/shared/interfaces/select-option';
import { dateDifference } from './date-helpers';
import { TFpnStyles } from '@/shared/types/fpn-svg-parsing-types';
import i18n from '@/i18n';
import { TOUR_CALENDAR_STATUS_COLOR_HEXES } from '@/shared/constants/tours';
import { CALL_LOG_MACHINE_COMPLETED_STATUS } from './call-log-status-filter';

export const propertyCategoryOptions: ISelectOption[] = [
  { text: 'Residential', value: 'residential' },
  { text: 'Commercial', value: 'commercial' },
  { text: 'Recreational', value: 'recreational' },
  { text: 'Other', value: 'other' },
];

export const residentialTypeOptions: ISelectOption[] = [
  { text: 'Multi-Unit', value: 'multi-unit' },
  { text: 'Single-Unit', value: 'single-unit' },
];

export const statuses: ISelectOption[] = [
  {
    value: 'enabled',
    text: 'Published',
  },
  {
    value: 'disabled',
    text: 'Not Published',
  },
];

export const baseStatuses: ISelectOption[] = [
  {
    value: 'enabled',
    text: 'Enabled',
  },
  {
    value: 'disabled',
    text: 'Disabled',
  },
];

export const promotionStatuses: ISelectOption[] = [
  {
    value: '',
    text: 'Select Status',
  },
  {
    value: 'enabled',
    text: 'In Progress',
  },
  {
    value: 'disabled',
    text: 'Disabled',
  },
  {
    value: 'expired',
    text: 'Expired',
  },
  {
    value: 'scheduled',
    text: 'Scheduled',
  },
];

export const statusesExpanded: ISelectOption[] = [
  ...statuses,
  {
    value: 'hidden',
    text: 'Hidden',
  },
];

export const blogNewsAndDocumentStatuses: ISelectOption[] = [
  {
    value: 'published',
    text: 'Published',
  },
  {
    value: 'hidden',
    text: 'Hidden',
  },
  {
    value: 'deleted',
    text: 'Deleted',
  },
];

export const propertyTypes: ISelectOption[] = [
  {
    value: '',
    text: 'Select Property Type',
  },
  {
    value: 'residential',
    text: 'Residential',
  },
  {
    value: 'commercial',
    text: 'Commercial',
  },
  {
    value: 'recreational',
    text: 'Recreational',
  },
  {
    value: 'other',
    text: 'Other',
  },
];

export const unitAvailabilityStatuses: ISelectOption[] = [
  {
    value: 1,
    text: 'Available',
  },
  {
    value: 2,
    text: 'Available on Date',
  },
  {
    value: 0,
    text: 'Unavailable',
  },
  {
    value: -1,
    text: 'Waiting List Only',
  },
];

export const unitRenovationStatuses: ISelectOption[] = [
  {
    value: null,
    text: 'Select',
  },
  {
    value: 'ready',
    text: 'Ready',
  },
  {
    value: 'under-repair',
    text: 'Under Repair',
  },
  {
    value: 'to-be-assessed',
    text: 'To Be Assessed',
  },
  {
    value: 'to-be-repaired',
    text: 'To Be Repaired',
  },
];

export const unitDepositType: ISelectOption[] = [
  {
    value: 'none',
    text: 'None',
  },
  {
    value: 'half-month',
    text: 'Half Month Rent',
  },
  {
    value: 'one-month',
    text: 'One Month Rent',
  },
  {
    value: 'two-months',
    text: 'Two Months Rent',
  },
  {
    value: 'other',
    text: 'Other',
  },
];

export const unitCommercialContractTypeOptions: ISelectOption[] = [
  {
    value: '',
    text: 'Select',
  },
  {
    value: 'Net',
    text: 'Net',
  },
  {
    value: 'Net-Net',
    text: 'Net-Net',
  },
  {
    value: 'Net-Net-Net',
    text: 'Net-Net-Net',
  },
  {
    value: 'Gross',
    text: 'Gross',
  },
  {
    value: 'Semi Gross',
    text: 'Semi Gross',
  },
];

export const unitLeaseTerm: ISelectOption[] = [
  {
    value: '1-month',
    text: 'One Month',
  },
  {
    value: '2-month',
    text: 'Two Months',
  },
  {
    value: '3-month',
    text: 'Three Months',
  },
  {
    value: '6-month',
    text: 'Six Months',
  },
  {
    value: '12-month',
    text: 'One Year',
  },
  {
    value: 'other',
    text: 'Other',
  },
];

export const propertyClassifications: ISelectOption[] = [
  {
    value: '',
    text: '',
  },
  {
    value: 'A',
    text: 'A',
  },
  {
    value: 'B',
    text: 'B',
  },
  {
    value: 'C',
    text: 'C',
  },
  {
    value: 'D',
    text: 'D',
  },
  {
    value: 'other',
    text: 'Other',
  },
];

export const notificationStatuses: Array<ISelectOption> = [
  { value: 'unlisted', text: 'Unlisted / No Consent' },
  { value: 'waiting', text: 'Waiting' },
  { value: 'unsubscribed', text: 'Unsubscribed' },
  { value: 'expired', text: 'Expired' },
  { value: 'sent', text: 'Sent' },
  { value: 'converted', text: 'Converted' },
  { value: 'removed', text: 'Removed' },
];

const syndicationPropertyTypes: Array<ISelectOption> = [
  { text: 'Residential', value: 'residential' },
  { text: 'Commercial', value: 'commercial' },
];

export const postTypes: Array<ISelectOption> = [
  { text: 'Bios', value: 'Bios' },
  { text: 'Blogs', value: 'Blog' },
  { text: 'Careers', value: 'Careers' },
  { text: 'Documents', value: 'Documents' },
  { text: 'FAQs', value: 'FAQs' },
  { text: 'News', value: 'News' },
  { text: 'Testimonials', value: 'Testimonials' },
];

export const postStatusOptions: Array<ISelectOption> = [
  { text: 'Published', value: 'published' },
  { text: 'Hidden', value: 'hidden' },
  { text: 'Not Published', value: 'private' },
];

export const propertyStatusOptions: Array<ISelectOption> = [
  ...baseStatuses,
  { text: 'Hidden', value: 'hidden' },
];
export const noYesOptions: Array<ISelectOption<number>> = [
  { text: 'Yes', value: 1 },
  { text: 'No', value: 0 },
];
export const maintenanceStatusOptions: Array<ISelectOption> = [
  { text: `${i18n.t('staticSelectOptions.requested')}`, value: 'requested' },
  { text: `${i18n.t('staticSelectOptions.workScheduled')}`, value: 'scheduled' },
  { text: `${i18n.t('staticSelectOptions.resolved')}`, value: 'resolved' },
  { text: `${i18n.t('staticSelectOptions.declined')}`, value: 'declined' },
  { text: `${i18n.t('staticSelectOptions.spam')}`, value: 'spam' },
];

export const pageVisibilityTypes: Array<ISelectOption> = [
  { value: 'enabled', text: 'Public' },
  { value: 'hidden', text: 'Hidden' },
  { value: 'disabled', text: 'Disabled' },
];

export const pageTypes: Array<ISelectOption> = [
  { value: null, text: 'Select a Page Type' },
  { value: 'page', text: 'Page' },
  { value: 'about', text: 'About' },
  { value: 'apartments', text: 'Apartments' },
  { value: 'bios', text: 'Bios' },
  { value: 'blogs', text: 'Blogs' },
  { value: 'careers', text: 'Careers' },
  { value: 'cities', text: 'Cities' },
  { value: 'commercial', text: 'Commercial' },
  { value: 'communities', text: 'Communities' },
  { value: 'condos', text: 'Condos' },
  { value: 'construction', text: 'Construction' },
  { value: 'contact', text: 'Contact' },
  { value: 'corporate', text: 'Corporate' },
  { value: 'documents', text: 'Documents' },
  { value: 'executive_suites', text: 'Executive Suites' },
  { value: 'faqs', text: 'Faqs' },
  { value: 'for_sale', text: 'For Sale' },
  { value: 'furnished_suites', text: 'Furnished Suites' },
  { value: 'gallery', text: 'Gallery' },
  { value: 'home', text: 'Home' },
  { value: 'hotels', text: 'Hotels' },
  { value: 'houses', text: 'Houses' },
  { value: 'industrial', text: 'Industrial' },
  { value: 'maintenance', text: 'Maintenance' },
  { value: 'marinas', text: 'Marinas' },
  { value: 'military', text: 'Military' },
  { value: 'motels', text: 'Motels' },
  { value: 'new_developments', text: 'New Developments' },
  { value: 'news', text: 'News' },
  { value: 'office', text: 'Office' },
  { value: 'other', text: 'Other' },
  { value: 'properties', text: 'Properties' },
  { value: 'provinces', text: 'Provinces' },
  { value: 'residential', text: 'Residential' },
  { value: 'retail', text: 'Retail' },
  { value: 'rooms', text: 'Rooms' },
  { value: 'seniors', text: 'Seniors' },
  { value: 'short_term_suites', text: 'Short Term Suites' },
  { value: 'site_search', text: 'Site Search' },
  { value: 'sitemap', text: 'Sitemap' },
  { value: 'students', text: 'Students' },
  { value: 'subdivisions', text: 'Subdivisions' },
  { value: 'testimonials', text: 'Testimonials' },
  { value: 'townhouses', text: 'Townhouses' },
  { value: 'unsubscribe', text: 'Unsubscribe' },
  { value: 'template_1', text: 'Template 1' },
  { value: 'template_2', text: 'Template 2' },
  { value: 'template_3', text: 'Template 3' },
  { value: 'template_4', text: 'Template 4' },
  { value: 'template_5', text: 'Template 5' },
];

export const callTrackingBaseLogStatuses: ISelectOption[] = [
  { value: 'completed', text: `${i18n.t('callTracking.logStatuses.completedCalls')}` },
  { value: 'no-answer', text: `${i18n.t('callTracking.logStatuses.noAnswerCalls')}` },
  { value: 'canceled', text: `${i18n.t('callTracking.logStatuses.canceledCalls')}` },
  { value: 'failed', text: `${i18n.t('callTracking.logStatuses.failedCalls')}` },
  { value: 'busy', text: `${i18n.t('callTracking.logStatuses.busyCalls')}` },
];

/** Completed calls answered by a machine; only offered to clients with IVR, where `answeredBy` is recorded. */
export const callTrackingMachineCompletedStatus: ISelectOption = {
  value: CALL_LOG_MACHINE_COMPLETED_STATUS,
  text: `${i18n.t('callTracking.logStatuses.machineCompletedCalls')}`,
};

export const callTrackingIVRStatuses: ISelectOption[] = [
  { value: 'ivr-answered', text: `${i18n.t('callTracking.logStatuses.ivrAnswered')}` },
  { value: 'ivr-timeout', text: `${i18n.t('callTracking.logStatuses.ivrTimeout')}` },
];

export const callTrackingMessageLogStatuses: ISelectOption[] = [
  { value: 'delivered', text: `${i18n.t('callTracking.logStatuses.deliveredMessages')}` },
  { value: 'received', text: `${i18n.t('callTracking.logStatuses.receivedMessages')}` },
  { value: 'undelivered', text: `${i18n.t('callTracking.logStatuses.undeliveredMessages')}` },
];

export const callTrackingLogTypes: ISelectOption[] = [
  { value: 'call', text: 'Calls' },
  { value: 'message', text: 'Messages' },
];

export const fsTypes: ISelectOption[] = [
  { value: 'career', text: `${i18n.t('formSettings.fieldTypes.career')}` },
  { value: 'contact', text: `${i18n.t('formSettings.fieldTypes.contact')}` },
  { value: 'generic', text: `${i18n.t('formSettings.fieldTypes.generic')}` },
  { value: 'inquiry', text: `${i18n.t('formSettings.fieldTypes.inquiry')}` },
  { value: 'maintenance', text: `${i18n.t('formSettings.fieldTypes.maintenanceRequest')}` },
  { value: 'waitinglist', text: `${i18n.t('formSettings.fieldTypes.waitingList')}` },
];

export const fsSubtypes: ISelectOption[] = [
  { value: 'text', text: `${i18n.t('formSettings.fieldTypes.plainText')}` },
  { value: 'number', text: `${i18n.t('formSettings.fieldTypes.number')}` },
  { value: 'date', text: `${i18n.t('formSettings.fieldTypes.date')}` },
  { value: 'tel', text: `${i18n.t('formSettings.fieldTypes.phoneNumber')}` },
  { value: 'email', text: `${i18n.t('formSettings.fieldTypes.email')}` },
  { value: 'password', text: `${i18n.t('formSettings.fieldTypes.password')}` },
  { value: 'hidden', text: `${i18n.t('formSettings.fieldTypes.hidden')}` },
];

export const fsSaveTypes: ISelectOption[] = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.na')}` },
  { value: 'name', text: `${i18n.t('formSettings.fieldTypes.name')}` },
  { value: 'first_name', text: `${i18n.t('formSettings.fieldTypes.firstName')}` },
  { value: 'last_name', text: `${i18n.t('formSettings.fieldTypes.lastName')}` },
  { value: 'email', text: `${i18n.t('formSettings.fieldTypes.email')}` },
  { value: 'phone', text: `${i18n.t('formSettings.fieldTypes.phone')}` },
  { value: 'property_toggle', text: `${i18n.t('formSettings.fieldTypes.propertyToggle')}` },
  { value: 'city_id', text: `${i18n.t('formSettings.fieldTypes.city')}` },
  { value: 'building_id', text: `${i18n.t('formSettings.fieldTypes.property')}` },
  { value: 'suite_type_id', text: `${i18n.t('formSettings.fieldTypes.unit')}` },
  { value: 'unit', text: `${i18n.t('formSettings.fieldTypes.unitNumber')}` },
  { value: 'num_bedrooms', text: `${i18n.t('formSettings.fieldTypes.numberOfBedrooms')}` },
  { value: 'move_in_date', text: `${i18n.t('formSettings.fieldTypes.moveInDate')}` },
  { value: 'category', text: `${i18n.t('formSettings.fieldTypes.maintenanceCategory')}` },
  { value: 'disclaimer', text: `${i18n.t('formSettings.fieldTypes.authorizationDisclaimer')}` },
  { value: 'career_id', text: `${i18n.t('formSettings.fieldTypes.career')}` },
  { value: 'comments', text: `${i18n.t('formSettings.fieldTypes.comments')}` },
  { value: 'inquiry_type', text: `${i18n.t('formSettings.fieldTypes.inquiryType')}` },
];

export const fsDataSourceTours: ISelectOption[] = [
  { value: 'suites', text: `${i18n.t('formSettings.fieldTypes.unitName')}` },
  { value: 'unit_number', text: `${i18n.t('formSettings.fieldTypes.unitNumber')}` },
  { value: 'num_bedrooms', text: `${i18n.t('formSettings.fieldTypes.numberOfBedrooms')}` },
];

export const fsValidations: ISelectOption[] = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.na')}` },
  { value: 'alphanumeric', text: 'Alphanumeric' },
  { value: 'numeric', text: 'Numeric' },
  { value: 'date', text: `${i18n.t('formSettings.fieldTypes.date')}` },
  { value: 'phone', text: `${i18n.t('formSettings.fieldTypes.phoneNumber')}` },
  { value: 'email', text: `${i18n.t('formSettings.fieldTypes.email')}` },
  { value: 'postal', text: 'Postal Code' },
];

export const fsBehaviours: ISelectOption[] = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.na')}` },
  { value: 'property-toggle', text: `${i18n.t('formSettings.fieldTypes.togglesInquiring')}` },
  { value: 'property-choice', text: `${i18n.t('formSettings.fieldTypes.selectsProperty')}` },
  { value: 'property-dependent', text: `${i18n.t('formSettings.fieldTypes.onlyShownIfInquiring')}` },
  { value: 'career-choice', text: `${i18n.t('formSettings.fieldTypes.selectsCareer')}` },
];

export const fsAutocomplete = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.on')}` },
  { value: 'off', text: `${i18n.t('formSettings.fieldTypes.off')}` },
];

export const fsExcludeField: ISelectOption[] = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.na')}` },
  { value: 'email', text: `${i18n.t('formSettings.fieldTypes.excludeFromEmail')}` },
  { value: 'inquiry', text: `${i18n.t('formSettings.fieldTypes.excludeFromInquiry')}` },
  { value: 'all', text: `${i18n.t('formSettings.fieldTypes.both')}` },
];

export const fsDataSourceTypes: ISelectOption[] = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.na')}` },
  { value: 'cities', text: `${i18n.t('formSettings.fieldTypes.citiesOffered')}` },
  { value: 'properties', text: `${i18n.t('formSettings.fieldTypes.properties')}` },
  { value: 'suites', text: `${i18n.t('formSettings.fieldTypes.units')}` },
  { value: 'careers', text: `${i18n.t('formSettings.fieldTypes.careers')}` },
];

export const fsDataSourceRestrictions: ISelectOption[] = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.na')}` },
  { value: 'residential', text: `${i18n.t('formSettings.fieldTypes.residential')}` },
  { value: 'commercial', text: `${i18n.t('formSettings.fieldTypes.commercial')}` },
];

export const fsFileRestrictions: ISelectOption[] = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.anyStandardFiles')}` },
  { value: 'images', text: `${i18n.t('formSettings.fieldTypes.imageFiles')}` },
  { value: 'documents', text: `${i18n.t('formSettings.fieldTypes.documents')}` },
  { value: 'pdf', text: `${i18n.t('formSettings.fieldTypes.pdfOnly')}` },
];

export const fsConditionalValueTypes: ISelectOption[] = [
  { value: '', text: `${i18n.t('formSettings.fieldTypes.exists')}` },
  { value: 'not_empty', text: `${i18n.t('formSettings.fieldTypes.hasAValue')}` },
  { value: 'empty', text: `${i18n.t('formSettings.fieldTypes.doesNotHaveAValue')}` },
  { value: 'equal', text: `${i18n.t('formSettings.fieldTypes.hasTheFollowingValue')}` },
  { value: 'not_equal', text: `${i18n.t('formSettings.fieldTypes.doesNotHaveTheFollowingValue')}` },
];

export const fsCaptchaTypes: ISelectOption[] = [
  { value: null, text: `${i18n.t('formSettings.fieldTypes.none')}` },
  { value: 'basic', text: `${i18n.t('formSettings.fieldTypes.basic')}` },
  { value: 'recaptcha', text: `${i18n.t('formSettings.fieldTypes.recaptcha')}` },
];

export const panelTypes: ISelectOption[] = [
  { value: 'multi-panel', text: 'Multi Panel' },
  { value: 'single-panel', text: 'Single Panel' },
];

export const panelAccessTypes: ISelectOption[] = [
  { value: null, text: 'Everyone' },
  { value: 'superuser', text: 'Super User' },
];

export const panelModels: ISelectOption[] = [
  { value: null, text: 'All Components' },
  { value: 'pages', text: 'Pages' },
  { value: 'cities_offered', text: 'City Pages' },
  { value: 'buildings', text: 'Properties' },
  { value: 'panels', text: 'Global Content' },
];
export const unitOrientations: ISelectOption[] = [
  { value: null, text: 'Select' },
  { value: 'north', text: 'North' },
  { value: 'northeast', text: 'North East' },
  { value: 'east', text: 'East' },
  { value: 'southeast', text: 'South East' },
  { value: 'south', text: 'South' },
  { value: 'southwest', text: 'South West' },
  { value: 'west', text: 'West' },
  { value: 'northwest', text: 'North West' },
];

const listingScoreRangeOptions: ISelectOption[] = [
  { value: 'poor', text: '0% - 49% (Poor)' },
  { value: 'fair', text: '50% - 69% (Fair)' },
  { value: 'good', text: '70% - 79% (Good)' },
  { value: 'veryGood', text: '80% - 90% (Very Good)' },
  { value: 'excellent', text: '91% + (Excellent)' },
];

const stateSelectOptions: ISelectOption[] = [
  { value: 'activated', text: 'Activated' },
  { value: 'deactivated', text: 'Deactivated' },
  { value: 'unavailable', text: 'Unavailable' },
];

export const selectDateOptions: ISelectOption[] = [
  { value: '', text: `${i18n.t('callTracking.filters.select')}` },
  { value: dateDifference.last7, text: `${i18n.t('callTracking.filters.last7Days')}` },
  { value: dateDifference.last14, text: `${i18n.t('callTracking.filters.last14Days')}` },
  { value: dateDifference.last30, text: `${i18n.t('callTracking.filters.last30Days')}` },
  { value: dateDifference.last60, text: `${i18n.t('callTracking.filters.last60Days')}` },
  { value: dateDifference.last90, text: `${i18n.t('callTracking.filters.last90Days')}` },
  { value: dateDifference.last180, text: `${i18n.t('callTracking.filters.last6Months')}` },
  { value: dateDifference.last360, text: `${i18n.t('callTracking.filters.last12Months')}` },
  { value: 'custom', text: `${i18n.t('callTracking.filters.customRange')}` },
];

export const promotedSubscriptionTags: ISelectOption[] = [
  { text: 'New', value: 'new' },
  { text: 'Free', value: 'free' },
  { text: 'Promo', value: 'promo' },
  { text: 'External Subscription', value: 'external' },
];

export const tourStatusOptions: ISelectOption[] = [
  { text: `${i18n.t('tours.statuses.requested')}`, value: 'requested' },
  { text: `${i18n.t('tours.statuses.confirmed')}`, value: 'confirmed' },
  { text: `${i18n.t('tours.statuses.cancelled')}`, value: 'cancel' },
  { text: `${i18n.t('tours.statuses.completed')}`, value: 'tour-completed' },
  { text: `${i18n.t('tours.statuses.noShow')}`, value: 'no-show' },
  { text: `${i18n.t('tours.statuses.declined')}`, value: 'denied' },
  { text: `${i18n.t('tours.statuses.leased')}`, value: 'leased' },
  { text: `${i18n.t('tours.statuses.unknown')}`, value: 'unknown' },
  { text: `${i18n.t('tours.statuses.closed')}`, value: 'closed' },
  { text: `${i18n.t('tours.statuses.notInterested')}`, value: 'not-interested' },
];

export const tourCalendarStatusOptions: ISelectOption[] = [
  { text: `${i18n.t('tours.statuses.confirmed')}`, value: 'confirmed', labelColor: TOUR_CALENDAR_STATUS_COLOR_HEXES.confirmed.border, labelBackgroundColor: TOUR_CALENDAR_STATUS_COLOR_HEXES.confirmed.background },
  { text: `${i18n.t('tours.statuses.requested')}`, value: 'requested', labelColor: TOUR_CALENDAR_STATUS_COLOR_HEXES.requested.border, labelBackgroundColor: TOUR_CALENDAR_STATUS_COLOR_HEXES.requested.background },
  { text: `${i18n.t('tours.statuses.cancelled')}`, value: 'cancel', labelColor: TOUR_CALENDAR_STATUS_COLOR_HEXES.cancel.border, labelBackgroundColor: TOUR_CALENDAR_STATUS_COLOR_HEXES.cancel.background },
  { text: `${i18n.t('tours.statuses.completed')}`, value: 'tour-completed', labelColor: TOUR_CALENDAR_STATUS_COLOR_HEXES['tour-completed'].border, labelBackgroundColor: TOUR_CALENDAR_STATUS_COLOR_HEXES['tour-completed'].background },
  { text: `${i18n.t('tours.statuses.closed')}`, value: 'closed', labelColor: TOUR_CALENDAR_STATUS_COLOR_HEXES.closed.border, labelBackgroundColor: TOUR_CALENDAR_STATUS_COLOR_HEXES.closed.background },
];

export const tourDurationOptions: ISelectOption[] = [
  { text: 'Select', value: null },
  { text: '15 min', value: 15 },
  { text: '30 min', value: 30 },
  { text: '45 min', value: 45 },
  { text: '60 min', value: 60 },
  { text: '75 min', value: 75 },
  { text: '90 min', value: 90 },
  { text: '120 min', value: 120 },
  { text: `${i18n.t('toursSettings.common.custom')}`, value: 'custom' },
];

export const tourDelayOptions: ISelectOption[] = [
  { text: '1hr', value: 1 },
  { text: '2hrs', value: 2 },
  { text: '4hrs', value: 4 },
  { text: '8hrs', value: 8 },
  { text: '12hrs', value: 12 },
  { text: '24hrs', value: 24 },
  { text: '48hrs', value: 48 },
  { text: `${i18n.t('toursSettings.common.custom')}`, value: null },
];

export const starRatingOptions: ISelectOption[] = [
  { text: 'one', value: 1 },
  { text: 'two', value: 2 },
  { text: 'three', value: 3 },
  { text: 'four', value: 4 },
  { text: 'five', value: 5 },
];

export const numBedrooms: ISelectOption[] = [
  { text: '0', value: 0 },
  { text: '1', value: 1 },
  { text: '2', value: 2 },
  { text: '3', value: 3 },
  { text: '4', value: 4 },
  { text: '5', value: 5 },
  { text: '6+', value: 6 },
];

export const calendarSyncActionOptions: ISelectOption[] = [
  { text: `${i18n.t('tours.settings.agents.single.calendarSync.actionOptions.fullSync')}`, value: 'sync' },
  { text: `${i18n.t('tours.settings.agents.single.calendarSync.actionOptions.pull')}`, value: 'pull' },
  { text: `${i18n.t('tours.settings.agents.single.calendarSync.actionOptions.push')}`, value: 'push' },
];

export const timezoneOptions: ISelectOption[] = [
  { value: 'Canada/Atlantic', text: 'Canada/Atlantic' },
  { value: 'Canada/Central', text: 'Canada/Central' },
  { value: 'Canada/Eastern', text: 'Canada/Eastern' },
  { value: 'Canada/Mountain', text: 'Canada/Mountain' },
  { value: 'Canada/Newfoundland', text: 'Canada/Newfoundland' },
  { value: 'Canada/Pacific', text: 'Canada/Pacific' },
  { value: 'Canada/Saskatchewan', text: 'Canada/Saskatchewan' },
  { value: 'Canada/Yukon', text: 'Canada/Yukon' },
  { value: 'US/Alaska', text: 'US/Alaska' },
  { value: 'US/Aleutian', text: 'US/Aleutian' },
  { value: 'US/Arizona', text: 'US/Arizona' },
  { value: 'US/Central', text: 'US/Central' },
  { value: 'US/East-Indiana', text: 'US/East-Indiana' },
  { value: 'US/Eastern', text: 'US/Eastern' },
  { value: 'US/Hawaii', text: 'US/Hawaii' },
  { value: 'US/Indiana-Starke', text: 'US/Indiana-Starke' },
  { value: 'US/Michigan', text: 'US/Michigan' },
  { value: 'US/Mountain', text: 'US/Mountain' },
  { value: 'US/Pacific', text: 'US/Pacific' },
  { value: 'US/Pacific-New', text: 'US/Pacific-New' },
  { value: 'US/Samoa', text: 'US/Samoa' },
  { value: 'UTC', text: 'UTC' },
];

export const dateRangeOptions: ISelectOption[] = [
  { value: '', text: `${i18n.t('callTracking.filters.select')}` },
  { value: dayjs().subtract(7, 'day').format('YYYY-MM-DD'), text: `${i18n.t('callTracking.filters.last7Days')}` },
  { value: dayjs().subtract(14, 'day').format('YYYY-MM-DD'), text: `${i18n.t('callTracking.filters.last14Days')}` },
  { value: dayjs().subtract(30, 'day').format('YYYY-MM-DD'), text: `${i18n.t('callTracking.filters.last30Days')}` },
  { value: dayjs().subtract(60, 'day').format('YYYY-MM-DD'), text: `${i18n.t('callTracking.filters.last60Days')}` },
  { value: dayjs().subtract(90, 'day').format('YYYY-MM-DD'), text: `${i18n.t('callTracking.filters.last90Days')}` },
  { value: dayjs().subtract(6, 'month').format('YYYY-MM-DD'), text: `${i18n.t('callTracking.filters.last6Months')}` },
  { value: dayjs().subtract(12, 'month').format('YYYY-MM-DD'), text: `${i18n.t('callTracking.filters.last12Months')}` },
  { value: 'custom', text: `${i18n.t('callTracking.filters.customRange')}` },
];

export const months: ISelectOption[] = [
  { value: 0, text: `${i18n.t('calendar.months.january')}` },
  { value: 1, text: `${i18n.t('calendar.months.february')}` },
  { value: 2, text: `${i18n.t('calendar.months.march')}` },
  { value: 3, text: `${i18n.t('calendar.months.april')}` },
  { value: 4, text: `${i18n.t('calendar.months.may')}` },
  { value: 5, text: `${i18n.t('calendar.months.june')}` },
  { value: 6, text: `${i18n.t('calendar.months.july')}` },
  { value: 7, text: `${i18n.t('calendar.months.august')}` },
  { value: 8, text: `${i18n.t('calendar.months.september')}` },
  { value: 9, text: `${i18n.t('calendar.months.october')}` },
  { value: 10, text: `${i18n.t('calendar.months.november')}` },
  { value: 11, text: `${i18n.t('calendar.months.december')}` },
];

export const monthsOptions: ISelectOption[] = [
  { value: '01', text: `${i18n.t('calendar.months.january')}` },
  { value: '02', text: `${i18n.t('calendar.months.february')}` },
  { value: '03', text: `${i18n.t('calendar.months.march')}` },
  { value: '04', text: `${i18n.t('calendar.months.april')}` },
  { value: '05', text: `${i18n.t('calendar.months.may')}` },
  { value: '06', text: `${i18n.t('calendar.months.june')}` },
  { value: '07', text: `${i18n.t('calendar.months.july')}` },
  { value: '08', text: `${i18n.t('calendar.months.august')}` },
  { value: '09', text: `${i18n.t('calendar.months.september')}` },
  { value: '10', text: `${i18n.t('calendar.months.october')}` },
  { value: '11', text: `${i18n.t('calendar.months.november')}` },
  { value: '12', text: `${i18n.t('calendar.months.december')}` },
];

export const weekdays: ISelectOption[] = [
  { value: 0, text: 'S' },
  { value: 1, text: 'M' },
  { value: 2, text: 'T' },
  { value: 3, text: 'W' },
  { value: 4, text: 'T' },
  { value: 5, text: 'F' },
  { value: 6, text: 'S' },
];

export const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const toneOptions = [
  { value: '', text: 'Select' },
  { value: 'friendly', text: 'Friendly' },
  { value: 'professional', text: 'Professional' },
  { value: 'modern', text: 'Modern' },
  { value: 'futuristic', text: 'Futuristic' },
  { value: 'poetic', text: 'Poetic' },
  { value: 'informative', text: 'Informative' },
  { value: 'humorous', text: 'Humorous' },
  { value: 'formal', text: 'Formal' },
  { value: 'conversational', text: 'Conversational' },
  { value: 'custom', text: 'Custom' },
];

export const demographicOptions = [
  { value: 'students', text: 'Students' },
  { value: 'young families', text: 'Young families' },
  { value: 'couples', text: 'Couple' },
  { value: 'extended-families', text: 'Extended Families' },
  { value: 'retirees', text: 'Retirees' },
  { value: 'young professionals', text: 'Young Professionals' },
];

export const FPNOldLayoutOptions: ISelectOption[] = [
  { value: 'default', text: 'Default' },
  { value: 'stacked', text: 'Stacked' },
  { value: 'stacked-alt', text: 'Stacked (alt)' },
  { value: 'stacked-no-listing', text: 'Stacked (No Listing)' },
];

export const FPNNewLayoutOptions: ISelectOption[] = [
  { value: 'new-default', text: 'Standard' },
  { value: 'new-stacked-tall', text: 'Stacked Tall' },
  { value: 'new-stacked-wide', text: 'Stacked Wide' },
];

export const FPNLayoutCommunityOptions: ISelectOption[] = [
  { value: 'community-standard', text: 'Community Standard' },
  { value: 'community-wide', text: 'Community Wide' },
];

export const FPNSelectALayoutOption: ISelectOption[] = [
  { value: '', text: 'Select a Layout' },
];

export const FPNAllLayoutOptions: ISelectOption[] = [
  ...FPNSelectALayoutOption,
  ...FPNOldLayoutOptions,
  ...FPNNewLayoutOptions,
  ...FPNLayoutCommunityOptions,
];

export const FPNLayoutOptions: Record<TFpnStyles, ISelectOption[]> = {
  '3d': FPNNewLayoutOptions,
  standard: FPNNewLayoutOptions,
  community: FPNLayoutCommunityOptions,
};

export const FPNStyleOptions: ISelectOption[] = [
  {
    text: 'Select a Style',
    value: '',
  },
  {
    text: 'Standard',
    value: 'standard',
  },
  {
    text: 'Community',
    value: 'community',
  },
  {
    text: '3D',
    value: '3d',
  },
];

export const FPNVersionOptions: ISelectOption[] = [
  { value: 'latest', text: 'Latest' },
  { value: 'v2.5', text: 'V2 (tables)' },
  { value: 'v1.5', text: 'V1 (tables)' },
  { value: 'v2', text: 'V2 (json)' },
  { value: 'v1', text: 'V1 (json)' },
];

export const FPNModalStyleOptions: ISelectOption[] = [
  { value: 'modal', text: 'Modal' },
  { value: 'contained', text: 'Contained' },
];

export const FPNTransform: ISelectOption[] = [
  {
    value: null,
    text: 'Select',
    selected: true,
  },
  {
    text: 'None',
    value: 'none',
    selected: false,
  },
  {
    text: 'Uppercase first letter',
    value: 'uppercase-first',
    selected: false,
  },
  {
    text: 'Uppercase all',
    value: 'uppercase',
    selected: false,
  },
  {
    text: 'Lowercase all',
    value: 'lowercase',
    selected: false,
  },
  {
    text: 'Currency',
    value: 'currency',
    selected: false,
  },
];
export const FPNStylesSelector: ISelectOption[] = [
  {
    value: 'selector1',
    text: 'Selector 1',
  },
  {
    value: 'selector2',
    text: 'Selector 2',
  },
  {
    value: 'selector3',
    text: 'Selector 3',
  },
  {
    value: 'selector4',
    text: 'Selector 4',
  },
];

export const FPNCardFieldsOptions: ISelectOption[] = [
  {
    text: 'Type name',
    value: 'type-name',
    selected: false,
  },
  {
    text: 'Number',
    value: 'number',
    selected: false,
  },
  {
    text: 'Bed',
    value: 'bed',
    selected: true,
  },
  {
    text: 'Bath',
    value: 'bath',
    selected: false,
  },
  {
    text: 'Deposit',
    value: 'deposit',
    selected: false,
  },
  {
    text: 'Sqft',
    value: 'sqft',
    selected: false,
  },
  {
    text: 'Available',
    value: 'available',
    selected: false,
  },
  {
    text: 'Den',
    value: 'den',
    selected: false,
  },
  {
    text: 'Floor',
    value: 'floor',
    selected: false,
  },
  {
    text: 'Furnished',
    value: 'furnished',
    selected: false,
  },
  {
    text: 'Short Term',
    value: 'shortterm',
    selected: false,
  },
  {
    text: 'Luxury',
    value: 'luxury',
    selected: false,
  },
  {
    text: 'Executive',
    value: 'executive',
    selected: false,
  },
  {
    text: 'Description',
    value: 'description',
    selected: false,
  },
  {
    text: 'Availability Date',
    value: 'availability',
    selected: false,
  },
  {
    text: 'Orientation',
    value: 'orientation',
    selected: false,
  },
];

export const FPNFilterFieldsOptions: ISelectOption[] = [
  {
    text: 'Bed',
    value: 'bed',
    selected: false,
  },
  {
    text: 'Bath',
    value: 'bath',
    selected: false,
  },
  {
    text: 'SqFt',
    value: 'sqft',
    selected: false,
  },
  {
    text: 'Rate',
    value: 'rate',
    selected: false,
  },
  {
    text: 'Available',
    value: 'available',
    selected: false,
  },
  {
    text: 'Den',
    value: 'den',
    selected: false,
  },
  {
    text: 'Furnished',
    value: 'furnished',
    selected: false,
  },
  {
    text: 'Short Term',
    value: 'short-term',
    selected: false,
  },
  {
    text: 'Luxury',
    value: 'luxury',
    selected: false,
  },
  {
    text: 'Executive',
    value: 'executive',
    selected: false,
  },
];

export const FPNFilterOptions: ISelectOption[] = [
  {
    text: 'Exact Match',
    value: 'exact',
  },
  {
    text: 'Min',
    value: 'min',
  },
  {
    text: 'Max',
    value: 'max',
  },
  {
    text: 'Between',
    value: 'between',
  },
];

export const unitFeatureOptions: ISelectOption[] = [
  { value: 'furnished', text: 'Furnished' },
  { value: 'luxury', text: 'Luxury' },
  { value: 'executive', text: 'Executive' },
  { value: 'waterfront', text: 'Waterfront' },
  { value: 'retirees', text: 'Retirees' },
];

export const textTransformModalTabOptions: ISelectOption[] = [
  { value: 'none', text: 'None' },
  { value: 'capitalize', text: 'Capitalize' },
  { value: 'uppercase-all', text: 'Uppercase All' },
  { value: 'lowercase-all', text: 'Lowercase All' },
  { value: 'currency', text: 'Currency' },
];

export const fpnCustomFieldTargetOptions: ISelectOption[] = [
  {
    text: 'Select Building or Unit Field',
    value: '',
  },
  { value: 'unit', text: 'Unit' },
  { value: 'building', text: 'Building' },
];

export const fpnColumnGroupOptions: ISelectOption[] = [
  { value: 'title', text: 'Title' },
  { value: 'body', text: 'Body' },
  { value: 'footer', text: 'Footer' },
];

export const fpnSiteEnvironmentOptions: ISelectOption[] = [
  { value: 'staging', text: 'Staging' },
  { value: 'production', text: 'Production' },
];

export const fpnNumberFormatOptions: ISelectOption[] = [
  {
    text: 'None',
    value: 'none',
  },
  {
    text: 'Currency',
    value: 'currency',
  },
];

export const jobType: ISelectOption[] = [
  { value: '', text: 'Select' },
  { value: 'full_time', text: 'Full Time' },
  { value: 'part_time', text: 'Part Time' },
  { value: 'contract', text: 'Contract' },
  { value: 'temporary', text: 'Temporary' },
  { value: 'not_specified', text: 'Not Specific' },
];

export const workType: ISelectOption[] = [
  { value: '', text: 'Select' },
  { value: 'to_be_discussed', text: 'To Be Discussed' },
  { value: 'salary', text: 'Salary' },
  { value: 'hourly', text: 'Hourly' },
  { value: 'commission', text: 'Commission' },
  { value: 'salary_with_commission', text: 'Salary with Commission' },
  { value: 'hourly_with_commission', text: 'Hourly with Commission' },
  { value: 'salary_plus_apartment_benefit', text: 'Salary Plus Apartment Benefit' },
  { value: 'apartment_benefit_only', text: 'Apartment Benefit Only' },
  { value: 'salary_plus_car_allowance', text: 'Salary Plus Car Allowance' },
  { value: 'salary_plus_commission_plus_housing', text: 'Salary Plus Commission Plus Housing' },
];

export const yesOrNo: ISelectOption[] = [
  { value: 'yes', text: 'Yes' },
  { value: 'no', text: 'No' },
];

export const bstkSyncRemovedUnitOptions: ISelectOption[] = [
  {
    text: 'Marked Unavailable',
    value: 'unavailable',
  },
  {
    text: 'Marked Unavailable and Disabled',
    value: 'unavailable-disabled',
  },
  {
    text: 'Do nothing',
    value: 'no-action',
  },
];

export const bstkSyncReAddedUnitOptions: ISelectOption[] = [
  ...baseStatuses,
  { text: 'Do nothing', value: 'no-action' },
];

export const bstkSyncRemovedBuildingOptions: ISelectOption[] = [
  {
    text: 'Disabled',
    value: 'disabled',
  },
  {
    text: 'Do nothing',
    value: 'no-action',
  },
];

export const bstkSyncIntervalOptions: ISelectOption[] = [
  {
    text: '24 Hours',
    value: 24,
  },
  {
    text: '6 Hours',
    value: 6,
  },
  {
    text: '3 Hours',
    value: 3,
  },
];
export const sortByOrderOptions: ISelectOption[] = [
  {
    text: 'Ascending',
    value: 'asc',
  },
  {
    text: 'Descending',
    value: 'desc',
  },
];

export const yesNoBooleanOptions: ISelectOption[] = [
  {
    text: 'Yes',
    value: true,
  },
  {
    text: 'No',
    value: false,
  },
];

export const leadsStatusOptions: ISelectOption[] = [
  { value: 'read-app', text: 'Read' },
  { value: 'unread', text: 'Unread' },
];

export const leadsQualityOptions: ISelectOption[] = [
  { value: [], text: 'None' },
  { value: ['-1'], text: 'Quality Leads' },
  { value: ['-2'], text: 'Other Leads' },
];

export const activeInquiriesFiltersCardFormOptions: ISelectOption[] = [
  { text: 'Date', value: 'dateMin' },
  { text: 'Properties', value: 'buildingId' },
  { text: '# of Beds/Baths', value: 'bedsBaths' },
  { text: 'Cities', value: 'cityId' },
  { text: 'Sources', value: 'source' },
  { text: 'Status', value: 'status' },
  { text: 'Quality Leads', value: 'qualityLeads' },
];

export const promotionsAllFiltersOptions: ISelectOption[] = [
  { text: 'Cities', value: 'cities' },
  { text: 'Properties', value: 'properties' },
  { text: 'Categories', value: 'categories' },
  { text: 'Status', value: 'status' },
  { text: 'Start & End', value: 'start-end' },
];

export const cardFormOptions: ISelectOption[] = [
  { text: 'Cities', value: 'cityId' },
  { text: 'Properties', value: 'propertyId' },
  { text: 'Categories', value: 'categoryId' },
  { text: 'Status', value: 'status' },
  { text: 'Start & End', value: 'startEndDate' },
];

export const toursWidgetTitleOptions: ISelectOption[] = [
  { text: `${i18n.t('toursSettings.propertySettings.widgetTitlePropertyOnly')}`, value: 'property' },
  { text: `${i18n.t('toursSettings.propertySettings.widgetTitleAddressOnly')}`, value: 'address' },
  { text: `${i18n.t('toursSettings.propertySettings.widgetTitleBoth')}`, value: 'both' },
];

export const callGroupEntryLanguageOptions: ISelectOption[] = [
  { text: 'English', value: 'en' },
  { text: 'French', value: 'fr' },
];

export const IVRHoursAvailabilityOptions: ISelectOption[] = [
  { text: `${i18n.t('ivr.callHours.availabilityOptions.closed')}`, value: 'closed' },
  { text: `${i18n.t('ivr.callHours.availabilityOptions.allDay')}`, value: 'all-day' },
  { text: `${i18n.t('ivr.callHours.availabilityOptions.custom')}`, value: 'custom' },
];

export const staticSelectOptions = {
  activeInquiriesFiltersCardFormOptions,
  jobType,
  workType,
  yesOrNo,
  numBedrooms,
  selectDateOptions,
  unitOrientations,
  baseStatuses,
  promotionStatuses,
  statuses,
  statusesExpanded,
  propertyTypes,
  blogNewsAndDocumentStatuses,
  unitAvailabilityStatuses,
  unitDepositType,
  unitLeaseTerm,
  unitCommercialContractTypeOptions,
  propertyClassifications,
  syndicationPropertyTypes,
  postStatusOptions,
  postTypes,
  propertyStatusOptions,
  noYesOptions,
  maintenanceStatusOptions,
  notificationStatuses,
  pageTypes,
  pageVisibilityTypes,
  callTrackingLogTypes,
  fsTypes,
  fsAutocomplete,
  fsBehaviours,
  fsConditionalValueTypes,
  fsDataSourceRestrictions,
  fsDataSourceTypes,
  fsExcludeField,
  fsFileRestrictions,
  fsSaveTypes,
  fsDataSourceTours,
  fsSubtypes,
  fsValidations,
  fsCaptchaTypes,
  panelTypes,
  panelAccessTypes,
  panelModels,
  listingScoreRangeOptions,
  stateSelectOptions,
  promotedSubscriptionTags,
  timezoneOptions,
  tourStatusOptions,
  tourCalendarStatusOptions,
  starRatingOptions,
  tourDurationOptions,
  calendarSyncActionOptions,
  toneOptions,
  demographicOptions,
  FPNAllLayoutOptions,
  FPNTransform,
  unitFeatureOptions,
  textTransformModalTabOptions,
  fpnSiteEnvironmentOptions,
  fpnNumberFormatOptions,
  bstkSyncRemovedUnitOptions,
  bstkSyncRemovedBuildingOptions,
  bstkSyncIntervalOptions,
  bstkSyncReAddedUnitOptions,
  yesNoBooleanOptions,
  sortByOrderOptions,
  leadsQualityOptions,
  leadsStatusOptions,
  promotionsAllFiltersOptions,
  toursWidgetTitleOptions,
};
